import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment/moment'
import i18n from 'simple-react-i18n'
import { groupBy, isNull, keys, mean, orderBy, round, uniq } from 'lodash'
import ReactDOMServer from 'react-dom/server'
import { Grid } from '@mui/material'
import PluviometryAction from '../../../actions/PluviometryAction'
import PluviometerDto from '../../../dto/PluviometerDto'
import DtoPluviometerStats from '../../../dto/DtoPluviometerStats'
import DtoPluvioMeasure from '../../../dto/measures/DtoPluvioMeasure'
import ProgressCard from '../../../../components/card/ProgressCard'
import MessageCard from '../../../../components/card/MessageCard'
import EChartV6Pluvio from '../../../../components/echart/EChartV6Pluvio'
import MUIChartTabs from '../../../../components/echart/MUIChartTabs'
import SimpleChartTabsPluvio from '../../../../components/echart/SimpleChartTabsPluvio'
import Line from '../../../../components/echart/series/Line'
import Bar from '../../../../components/echart/series/Bar'
import Scatter from '../../../../components/echart/series/Scatter'
import Candlestick from '../../../../components/echart/series/Candlestick'
import MultiBand from '../../../../components/echart/series/MultiBand'
import { STATION_TYPE_NAME } from '../../../../station/constants/StationConstants'
import {
    exportExcelIcon,
    exportPictureIcon,
    histogramIcon,
    lineIcon,
} from '../../../../components/echart/EChartUtils'
import useUpdateEffect from '../../../../utils/customHook/useUpdateEffect'
import { exportFile } from '../../../../utils/ExportDataUtil'
import { hasValue, nFormatter } from '../../../../utils/NumberUtil'
import { enumerateBetweenDates, getDate, getDateWithHour, getFullDate } from '../../../../utils/DateUtil'
import { CIVIL_YEAR, HISTO } from '../../../../alerting/constants/ChartFollowContants'
import { statusIcon } from '../../../../utils/StatusUtil'
import { getLabel } from '../../../../utils/StoreUtils'
import { getQualifications, getStatuses, UNQUALIFIED } from '../../../../utils/QualityUtils'
import { generateGradient, getColorCircleEvent, getColorFromPalette2, getEventColor, getRGBColor } from '../../../../utils/ColorUtil'
import { chunkWithWords } from '../../../../utils/StringUtil'
import CustomEventDuration from '../../../../components/echart/series/CustomEventDuration'
import { getI18nOrLabel } from '../../../../utils/StringUtil'
import { calculatePercentile90 } from '../../../../utils/AnalyseUtils'
import {
    ALL,
    AUTO,
    SUM_DAY,
    SUM_DECADE,
    SUM_HOUR,
    SUM_MONTH,
    SUM_WEEK,
    SUM_YEAR,
} from '../../../constants/PluvioOptionConstant'
import {
    DATATYPE_RAIN,
    getAutoCmumul,
    getGroupFunc,
    getGroupFuncFrequencyStat,
    tooltipDatePluvio,
} from '../../../../utils/PluviometryUtils'
import { getFrequencyStats } from '../../../../piezometry/components/suivi/utils/SuiviStatsUtils'

const BAR = 'bar'
const LINE = 'line'
const ENVELOPPE_MIN = 'ENVELOPPE_MIN'
const ENVELOPPE_MAX = 'ENVELOPPE_MAX'
const ENVELOPE_STACK = 'envelope'

const INITIAL_DISPLAY_MODES = {
    auto: true,
    all: false,
    sumHour: false,
    sumDay: false,
    sumWeek: false,
    sumDecade: false,
    sumMonth: false,
    sumYear: false,
    personalizedGrouping: false,
}

const groupFuncToLabel = (groupFunc) => {
    switch (groupFunc) {
        case SUM_HOUR: return i18n.hourTotal
        case SUM_DAY: return i18n.dailyTotal
        case SUM_WEEK: return i18n.weeklyTotal
        case SUM_DECADE: return i18n.decadeTotal
        case SUM_MONTH: return i18n.monthlyTotal
        case SUM_YEAR: return i18n.yearTotal
        default: return groupFunc || ''
    }
}

// Port direct de `getChartParams` du dashboard (PluviometerChartPanel.js:806-911) :
// - Aligne chartMinDate / chartMaxDate sur les frontières calendaires du cumul
//   (startOf('month') / endOf('month') pour SUM_MONTH, idem week/year).
// - Enumère les "dates de catégorie" pour l'axe X via enumerateBetweenDates.
// - Retourne le format moment correspondant au cumul (MM/YYYY pour SUM_MONTH, etc.).
// Sert à reproduire à l'identique le rendu axe X + tooltip du dashboard.
const computeChartParams = (cumulPluvio, minDate, maxDate, time, firstMeasureDate) => {
    // En HISTO (et tout mode sans minDate), on cale sur la 1re mesure pour couvrir
    // tout l'historique — calque hydro/piezo (`minDate || measures[0]?.date || now`).
    // Sans ce fallback, `now - 1 an` limitait l'affichage aux 2 dernières années.
    const tmpMinDate = minDate ?? firstMeasureDate ?? moment().subtract(1, 'year').valueOf()
    const tmpMaxDate = maxDate ?? moment().valueOf()
    const chartMinDateWithoutCumul = moment(time === HISTO ? moment(tmpMinDate).startOf('year').valueOf() : tmpMinDate).startOf('day').valueOf()
    const chartMaxDateWithoutCumul = moment(time === HISTO ? moment(tmpMaxDate).endOf('year').valueOf() : tmpMaxDate).endOf('day').valueOf()

    const [chartMinDate, chartMaxDate] = (() => {
        switch (cumulPluvio) {
            case SUM_WEEK:
                return [moment(chartMinDateWithoutCumul).startOf('week').valueOf(), moment(chartMaxDateWithoutCumul).endOf('week').valueOf()]
            case SUM_MONTH:
                return [moment(chartMinDateWithoutCumul).startOf('month').valueOf(), moment(chartMaxDateWithoutCumul).endOf('month').valueOf()]
            case SUM_YEAR:
                return [moment(chartMinDateWithoutCumul).startOf('year').valueOf(), moment(chartMaxDateWithoutCumul).endOf('year').valueOf()]
            default:
                return [chartMinDateWithoutCumul, chartMaxDateWithoutCumul]
        }
    })()

    const daysTotal = moment(chartMaxDate).diff(moment(chartMinDate), 'days')
    const [categoryMoments, categoryFormat] = (() => {
        switch (cumulPluvio) {
            case SUM_HOUR: return [enumerateBetweenDates(chartMinDate, chartMaxDate, 'hours'), 'DD/MM HH[h]']
            case SUM_DAY: return [enumerateBetweenDates(chartMinDate, chartMaxDate, 'days'), daysTotal > 365 ? 'DD/MM/YYYY' : 'DD/MM']
            case SUM_WEEK: return [enumerateBetweenDates(chartMinDate, chartMaxDate, 'weeks'), daysTotal > 365 ? '[S]w GGGG' : '[S]w']
            case SUM_DECADE: return [enumerateBetweenDates(chartMinDate, chartMaxDate, 'days'), daysTotal > 365 ? 'DD/MM/YYYY' : 'DD/MM']
            case SUM_MONTH: return [enumerateBetweenDates(chartMinDate, chartMaxDate, 'months'), 'MM/YYYY']
            case SUM_YEAR: return [enumerateBetweenDates(chartMinDate, chartMaxDate, 'years'), 'YYYY']
            default: return [enumerateBetweenDates(chartMinDate, chartMaxDate, 'days'), daysTotal > 365 ? 'DD/MM/YYYY' : 'DD/MM']
        }
    })()

    return {
        chartMinDate,
        chartMaxDate,
        categoryDates: categoryMoments.map(d => d.format(categoryFormat)),
        categoryFormat,
        daysTotal,
    }
}

// Format de data pour ECharts. En mode BAR (axe category), le premier élément
// du tuple est la date formatée selon `categoryFormat` (matche les labels de
// l'axe category). En mode LINE, c'est le timestamp brut (axe time caché).
const toSeriesData = (measures, isBar, categoryFormat) => measures.map(m => ({
    value: [isBar ? getDate(m.date, categoryFormat) : m.date, m.value],
    rawMeasure: m,
}))

// Enveloppe min/max : 2 séries Line empilées avec areaStyle uniquement sur la
// supérieure → la zone remplie apparaît entre min et max. Alignement par DATE
// car selon le groupFunc le backend peut renvoyer min et max avec des dates
// désalignées (buckets vides omis d'un côté pas de l'autre). Pas de fallback
// sur 0 : si la date manque côté min, on remonte null pour que la zone ne soit
// pas gonflée artificiellement à `min + max`.
const buildEnvelopeSeries = (minMeasures, maxMeasures, name, color) => {
    const minByDate = new Map(minMeasures.map(m => [m.date, m.value]))
    const lowerData = minMeasures.map(m => ({ value: [m.date, m.value], rawMeasure: m }))
    const upperData = maxMeasures.map(m => {
        const minValue = minByDate.get(m.date)
        const stackedValue = (!isNull(m.value) && !isNull(minValue)) ? (m.value - minValue) : null
        return { value: [m.date, stackedValue], rawMeasure: m, realValue: m.value }
    })
    return [
        {
            name,
            type: 'line',
            stack: ENVELOPE_STACK,
            data: lowerData,
            lineStyle: { opacity: 0 },
            symbol: 'none',
            showSymbol: false,
            connectNulls: true,
            silent: true,
            tooltip: { show: false },
            z: -1,
        },
        {
            name,
            type: 'line',
            stack: ENVELOPE_STACK,
            data: upperData,
            lineStyle: { opacity: 0 },
            areaStyle: { color, opacity: 0.35 },
            symbol: 'none',
            showSymbol: false,
            connectNulls: true,
            silent: true,
            tooltip: { show: false },
            z: -1,
        },
    ]
}

// Construit les séries ECharts pour UN modèle prévisionnel (pred + ses mesures).
// Adaptation du getModelSeries du dashboard pour un axe X `time` (vs `category`).
// Retourne des instances _Line/_Bar/_Scatter/_Candlestick/_MultiBand — converties
// en JSON ECharts plat au moment du spread dans le tableau `series`.
// `predIdx` sert de graine de couleur quand le modèle n'impose pas la sienne, pour que
// plusieurs modèles affichés simultanément aient des couleurs distinctes.
const buildPredSeries = (pred, measures, isBar, unit, categoryFormat, categoryDates, predIdx) => {
    if (!pred || !measures?.length) return []

    const toEchartMeasure = (v, measure, labelSerie, color) => ({
        value: [isBar ? getDate(measure.date, categoryFormat) : measure.date, v, unit, measure, labelSerie, color],
    })

    // Branch 1 — Seeds / Saisonnières / GEFS : Scatter (moyenne) + Candlestick (P25/P75 + extrêmes)
    if (pred.source?.includes('Seeds') || pred.source?.includes('Saisonnières') || pred.source?.includes('GEFS')) {
        const byDate = groupBy(measures, 'date')
        const color = pred.model?.color ?? getColorFromPalette2(predIdx)

        const averageMeasures = Object.keys(byDate).map(key => {
            const samples = byDate[key]
            const avg = round(mean(samples.map(v => v.value)), 3)
            return toEchartMeasure(avg, { ...samples[0], value: avg }, pred.source, color)
        })

        // En BAR (axe category), la data est indexée par categoryDates (4 valeurs : open, close, low, high).
        // En LINE (axe time), la data inclut le timestamp en tête (5 valeurs).
        const candleDic = Object.keys(byDate).reduce((acc, key) => {
            const samples = byDate[key]
            const sortedValues = orderBy(samples.map(v => v.value), v => v)
            const quartile = [
                calculatePercentile90(sortedValues, 0.25),
                calculatePercentile90(sortedValues, 0.75),
                sortedValues[0],
                sortedValues[sortedValues.length - 1],
            ]
            const dictKey = isBar ? getDate(samples[0].date, categoryFormat) : samples[0].date
            acc[dictKey] = isBar ? quartile : [samples[0].date, ...quartile]
            return acc
        }, {})
        const candleData = isBar
            ? categoryDates.map(d => candleDic[d] ?? [NaN, NaN, NaN, NaN])
            : Object.values(candleDic)

        return [
            Scatter({
                data: averageMeasures,
                name: pred.source,
                color,
                itemStyle: { normal: { color: 'black' } },
                zlevel: 100,
            }),
            Candlestick({
                data: candleData,
                name: pred.source,
                color,
                barMinWidth: 8,
                barMaxWidth: 16,
                large: false,
                itemStyle: {
                    normal: {
                        color,
                        color0: color,
                        borderColor: 'black',
                        borderColor0: 'black',
                    },
                },
                showSymbol: false,
            }),
        ]
    }

    // Branch 2 — Line / Bar (selon affichage main) ou MultiBand (si doutMin/doutMax)
    const allSeries = groupBy(measures, 'serieName')
    const seriesKeys = Object.keys(allSeries)
    const nbLineSeries = seriesKeys.filter(key => hasValue(allSeries[key][0].value)).length
    const nbSeries = seriesKeys.length

    return seriesKeys.flatMap((serieName, idx) => {
        const ordered = orderBy(allSeries[serieName], 'date')
        const labelSerie = `${pred.source}${serieName && serieName !== 'undefined' ? ` - ${getI18nOrLabel(serieName)}` : ''}`

        // Bande de doute (MultiBand stacké) — on masque la lower band du tooltip
        // pour ne pas dupliquer l'entrée (lower = même zone visuelle que upper).
        if (hasValue(ordered[0].doubtMin)) {
            const bandColor = pred.model?.color || getColorFromPalette2(predIdx)
            const lower = {
                showSymbol: false,
                color: bandColor,
                data: ordered.map(m => toEchartMeasure(m.doubtMin, m, labelSerie, bandColor)),
                name: labelSerie,
                noTooltip: true,
            }
            const upper = {
                showSymbol: false,
                color: bandColor,
                data: ordered.map(m => toEchartMeasure(m.doubtMax, m, labelSerie, bandColor)),
                name: labelSerie,
            }
            return [MultiBand({
                bands: [lower, upper],
                noSort: true,
                noGap: true,
                stack: `${pred.source}${idx}`,
            })]
        }

        // Line ou Bar simple
        const color = nbLineSeries > 1 ? getColorFromPalette2(idx) : (pred.model?.color || getColorFromPalette2(predIdx))
        return (isBar ? Bar : Line)({
            data: ordered.map(m => toEchartMeasure(m.value, m, labelSerie, color)),
            name: labelSerie,
            color,
            connectNulls: true,
            lineStyle: {
                normal: {
                    color,
                    width: pred.model?.lineWidth || 2,
                    type: pred.model?.lineType || 'dashed',
                    opacity: pred.model?.lineOpacity || 1,
                },
            },
            itemStyle: { normal: { color } },
            showSymbol: false,
            barGap: '0%',
            barWidth: `${40 / nbSeries}%`,
        })
    })
}

// Itère sur TOUS les modèles appliqués du type de donnée courant → plusieurs courbes
// simultanées. `selectedPred`/`predMeasures` sont indexés par clé complète
// (pluvioId:typeId:source) : indexés par typeId (ancien fork), plusieurs modèles d'un
// même type s'écrasaient et une seule courbe s'affichait.
const buildModelSeries = (modelData, typeId, isBar, unit, categoryFormat, categoryDates) => {
    const selectedPred = modelData?.selectedPred ?? {}
    const predMeasures = modelData?.predMeasures ?? {}
    return Object.keys(selectedPred)
        .filter(key => selectedPred[key]?.typeId === typeId && predMeasures[key]?.length)
        .flatMap((key, idx) => buildPredSeries(selectedPred[key], predMeasures[key], isBar, unit, categoryFormat, categoryDates, idx))
}

const buildFrequencySeries = (statsMeasures, isBar, categoryFormat) => {
    const allKeys = keys(statsMeasures)
    const sortedHigh = orderBy(allKeys.filter(k => k.endsWith('_H')), k => parseInt(k.split('_')[1], 10), ['desc'])
    const sortedLow = orderBy(allKeys.filter(k => k.endsWith('_S')), k => parseInt(k.split('_')[1], 10), ['asc'])
    const frequencyStatsList = getFrequencyStats(null, null, true)
    return [...sortedHigh, ...sortedLow].map(key => {
        const infoStat = frequencyStatsList.find(f => f.code === key)
        return {
            name: infoStat.name,
            type: 'line',
            z: 1,
            data: statsMeasures[key].map(sm => ({ value: [isBar ? getDate(sm[0], categoryFormat) : sm[0], sm[1]] })),
            connectNulls: false,
            showSymbol: false,
            symbol: 'none',
            itemStyle: { color: infoStat.color },
            lineStyle: { color: infoStat.color, width: 1, type: 'dashed' },
            areaStyle: { color: infoStat.color, opacity: 1 },
            emphasis: { disabled: true },
        }
    })
}

const getYScaleConfig = (chartOptions) => {
    if (chartOptions.yScaleMode !== 'manual') {
        return {}
    }
    const { yMin, yMax, yInterval } = chartOptions
    const config = {
        min: hasValue(yMin) ? yMin : undefined,
        max: hasValue(yMax) ? yMax : undefined,
    }
    if (!hasValue(yInterval) || yInterval <= 0) {
        return config
    }
    if (hasValue(yMin) && hasValue(yMax)) {
        return { ...config, interval: (yMax - yMin) / yInterval }
    }
    return { ...config, splitNumber: yInterval }
}

const Graphic = ({
    pluviometer,
    primary,
    enveloppeMin,
    enveloppeMax,
    showEnveloppe,
    minDate,
    maxDate,
    cumulPluvio,
    statsMeasures,
    seriesYears,
    thresholdLines,
    modelData,
    chartOptions = {},
    displayEvents = true,
    stationEvents = [],
    associationsSeries = [],
    associationsAxis = [],
    time,
}) => {
    const contributors = useSelector(state => state.ContributorReducer.contributors)

    const defaultChartType = primary.infoStat?.typeId === DATATYPE_RAIN ? BAR : LINE
    const [typeLineValue, setTypeLineValue] = useState(defaultChartType)
    const isBar = typeLineValue === BAR

    const unit = primary.infoStat?.unit
    const unitLabel = unit ? `[${unit}]` : ''
    const color = primary.infoStat?.color || 'blue'

    const cParams = computeChartParams(cumulPluvio, minDate, maxDate, time, primary.measures[0]?.date)
    const { chartMinDate, chartMaxDate, categoryDates, categoryFormat } = cParams
    const dataXAxisIndex = isBar ? 0 : 1

    const codepoints = uniq(primary.measures.map(m => m.codepoint).filter(cp => cp !== null))
    const isMultiPoint = codepoints.length > 1
    const isTemperature = primary.infoStat?.label === i18n.temperature
    const pointPalette = isMultiPoint ? generateGradient(color, '#FFFFFF', Math.max(codepoints.length, 2)) : []

    const getCodepointColor = (cp, idx) => {
        if (isTemperature && cp === 31) return 'blue'
        if (isTemperature && cp === 32) return 'red'
        return pointPalette[idx] ?? getColorFromPalette2(idx)
    }

    const hasModelMeasures = modelData?.predMeasures
        ? Object.values(modelData.predMeasures).some(arr => arr?.length > 0)
        : false
    const primaryBarWidth = hasModelMeasures ? '40%' : '80%'

    const measuresByCodepoint = isMultiPoint ? groupBy(primary.measures, 'codepoint') : {}
    const primarySeriesList = isMultiPoint
        ? codepoints.map((cp, idx) => {
            const cpMeasures = measuresByCodepoint[cp] ?? []
            const pointName = pluviometer.link_pointPrels?.find(p => p.point === parseInt(cp, 10))?.name ?? 'Point inconnu'
            const serieName = `${primary.infoStat?.label ?? pluviometer.name} - ${pointName}`
            const serieColor = getCodepointColor(cp, idx)
            return {
                name: serieName,
                type: typeLineValue,
                z: 3,
                xAxisIndex: dataXAxisIndex,
                showSymbol: false,
                symbol: 'none',
                connectNulls: false,
                itemStyle: { color: serieColor },
                lineStyle: { color: serieColor },
                data: toSeriesData(cpMeasures, isBar, categoryFormat),
                barGap: '-100%',
                barWidth: `${80 / codepoints.length}%`,
            }
        })
        : [{
            name: primary.infoStat?.label ?? pluviometer.name,
            type: typeLineValue,
            z: 3,
            xAxisIndex: dataXAxisIndex,
            barWidth: primaryBarWidth,
            barGap: '0%',
            showSymbol: false,
            symbol: 'none',
            connectNulls: false,
            itemStyle: { color },
            lineStyle: { color },
            data: toSeriesData(primary.measures, isBar, categoryFormat),
        }]

    const pastYearsSeries = seriesYears.map(s => ({
        name: s.name,
        type: typeLineValue,
        z: 3,
        xAxisIndex: dataXAxisIndex,
        barWidth: primaryBarWidth,
        showSymbol: false,
        symbol: 'none',
        connectNulls: !!s.connectNulls,
        itemStyle: { color: s.color },
        lineStyle: { color: s.color },
        data: toSeriesData(s.measures, isBar, categoryFormat),
    }))

    const envelopeSeries = showEnveloppe
        ? buildEnvelopeSeries(enveloppeMin.measures, enveloppeMax.measures, i18n.enveloppeMinMax, color).map(s => ({ ...s, xAxisIndex: 1 }))
        : []
    const frequencySeries = buildFrequencySeries(statsMeasures, isBar, categoryFormat).map(s => ({ ...s, xAxisIndex: dataXAxisIndex }))

    const thresholdSeries = thresholdLines.map(t => {
        const s = (typeof t?.getJson === 'function') ? t.getJson() : t
        return { ...s, xAxisIndex: dataXAxisIndex }
    })

    const rawModelSeries = buildModelSeries(modelData, primary.infoStat?.typeId, isBar, unit, categoryFormat, categoryDates)
    const modelSeries = rawModelSeries.flatMap(s => {
        if (typeof s?.getJson !== 'function') return [{ ...s, xAxisIndex: dataXAxisIndex }]
        const json = s.getJson()
        const arr = Array.isArray(json) ? json : [json]
        return arr.map(x => ({ ...x, xAxisIndex: dataXAxisIndex }))
    })

    const filteredEvents = (displayEvents && stationEvents)
        ? stationEvents.filter(e => {
            if (e.eventType === 'T') return false
            if (!e.date) return false
            const eventDate = getDateWithHour(e.date, e.eventHour).valueOf()
            // eslint-disable-next-line eqeqeq
            return eventDate >= chartMinDate && eventDate <= chartMaxDate && e.graph == '1'
        })
        : []
    const hasEvents = filteredEvents.length > 0

    const eventXAxisIndex = 2
    const eventYAxisIndex = 2

    const eventTooltip = {
        trigger: 'item',
        formatter: (params) => {
            const event = params.value?.[2] ?? params.data?.value?.[2]
            if (!event) return ''
            const startDate = getFullDate(getDateWithHour(event.date ?? event.startDate, event.eventHour).valueOf())
            const dateLine = event.endDate ? `${startDate} - ${getFullDate(event.endDate)}` : startDate
            const comment = event.comment ? chunkWithWords(event.comment, 40).replaceAll('\n', '<br />') : i18n.event
            return `${dateLine}<br />${getColorCircleEvent(event.eventType)} ${comment}`
        },
    }

    const eventDurationOptions = {
        id: 'events_duration',
        tooltip: eventTooltip,
        itemStyle: { normal: { opacity: 0.5 } },
        xAxisIndex: eventXAxisIndex,
        yAxisIndex: eventYAxisIndex,
    }

    const eventGraphInstances = hasEvents
        ? [
            CustomEventDuration({
                ...eventDurationOptions,
                name: i18n.events,
                serieId: 'events',
                data: filteredEvents.map(e => {
                    const startTs = e.startDate || getDateWithHour(e.date, e.eventHour).valueOf()
                    const endTs = e.eventType === 'S' && !e.endDate ? moment().valueOf() : (e.endDate || startTs)
                    return {
                        value: [startTs, endTs, e],
                        itemStyle: { color: getRGBColor(getEventColor(e.eventType)) },
                    }
                }),
                renderItem: (_, api) => {
                    const start = api.coord([api.value(0), 0])
                    const end = api.coord([api.value(1), 0])
                    const height = api.size([0, 1])[1]
                    return {
                        type: 'rect',
                        shape: {
                            x: start[0],
                            y: start[1] - height / 2,
                            width: Math.max(end[0] - start[0], 0),
                            height,
                        },
                        style: api.style(),
                    }
                },
            }),
            CustomEventDuration({
                id: 'events_marker',
                name: i18n.events,
                serieId: 'events',
                xAxisIndex: eventXAxisIndex,
                yAxisIndex: eventYAxisIndex,
                data: filteredEvents.map(e => ({
                    value: [
                        getDateWithHour(e.date ?? e.startDate, e.eventHour).valueOf(),
                        1,
                        e,
                    ],
                    itemStyle: { color: getRGBColor(getEventColor(e.eventType)) },
                    tooltip: eventTooltip,
                })),
                renderItem: (_, api) => {
                    const center = api.coord([api.value(0), 0])
                    const height = api.size([0, 1])[1]
                    const barWidth = 10
                    return {
                        type: 'rect',
                        shape: {
                            x: center[0] - barWidth / 2,
                            y: center[1] - height / 2,
                            width: barWidth,
                            height,
                        },
                        style: api.style(),
                    }
                },
            }),
        ]
        : []
    const eventSeries = eventGraphInstances.map(s => (typeof s?.getJson === 'function' ? s.getJson() : s))

    const associationYAxisStart = hasEvents ? 3 : 1
    const associationYAxes = associationsAxis.map((ax, idx) => ({
        type: 'value',
        name: ax.name,
        nameLocation: 'middle',
        nameGap: 50,
        gridIndex: 0,
        position: 'right',
        offset: idx * 60,
        min: hasValue(ax.min) ? ax.min : undefined,
        max: hasValue(ax.max) ? ax.max : undefined,
        axisLine: { show: true },
        axisTick: { show: true },
        splitLine: { show: false },
        inverse: !!ax.isPluvio, // axe pluvio inversé : barres pendantes depuis le haut
    }))

    const associationSeriesProcessed = associationsSeries.flatMap(s => {
        const json = typeof s?.getJson === 'function' ? s.getJson() : s
        if (!json) return []
        const obj = s?.obj ?? json
        const isAssocPluvio = !!obj.isPluvio
        const isFirstAxis = !!obj.isFirstAxis
        const axisName = obj.axisName
        const yAxisIdx = isFirstAxis ? 0 : associationYAxisStart + Math.max(0, associationsAxis.findIndex(ax => ax.name === axisName))
        const assocXAxisIndex = isAssocPluvio ? 0 : 1
        const convertedData = (json.data || []).map(d => {
            const v = d.value || d
            if (isAssocPluvio && typeof v[0] === 'number') {
                return { ...d, value: [getDate(v[0], categoryFormat), ...v.slice(1)] }
            }
            return d
        })
        return [{
            ...json,
            data: convertedData,
            xAxisIndex: assocXAxisIndex,
            yAxisIndex: yAxisIdx,
        }]
    })

    const series = [...envelopeSeries, ...primarySeriesList, ...pastYearsSeries, ...frequencySeries, ...thresholdSeries, ...modelSeries, ...eventSeries, ...associationSeriesProcessed]

    const getExportData = () => {
        const dataExport = primary.measures.map(d => ({
            stationCode: { value: pluviometer.code },
            stationName: { value: pluviometer.name },
            date: { value: getFullDate(d.date), format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date' },
            value: { value: d.value, format: '0.00', cellType: 'number' },
            type: { value: primary.infoStat.label },
            status: { value: getLabel(getStatuses(), d.status), cellType: 'string' },
            qualification: { value: getLabel(getQualifications(), d.qualification || UNQUALIFIED), cellType: 'string' },
            producer: { value: getLabel(contributors, d.producer, 'mnemonique'), cellType: 'string' },
        }))
        if (dataExport.length) {
            dataExport[0].headers = ['stationCode', 'stationName', 'date', 'value', 'type', 'status', 'qualification', 'producer']
        }
        return dataExport
    }

    const legendData = uniq(series.map(s => s.name).filter(Boolean)).map(name => (
        name === i18n.events
            ? { name, icon: 'pin', itemStyle: { color: '#5c5c5c', opacity: 1 } }
            : name
    ))

    const option = {
        title: {
            text: `[${pluviometer.code}]${pluviometer.name ? `   -   [${pluviometer.name}]` : ''}`,
            top: 25,
            left: 'center',
        },
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const filtered = (params ?? []).filter(p =>
                    p.componentSubType !== 'candlestick'
                    && !p.data?.noTooltip
                    && !p.seriesId?.startsWith('events_'),
                )
                if (!filtered.length) return ''
                const isBarParams = filtered[0]?.axisType?.includes('category')
                const ref = filtered[0]
                const refDate = ref?.data?.rawMeasure?.date
                    ?? (typeof ref?.value?.[3] === 'object' ? ref.value[3]?.date : undefined)
                    ?? ref?.value?.[0]
                const dateStr = isBarParams ? ref.value?.[0] : tooltipDatePluvio(refDate, cumulPluvio)
                const hoveredCategory = params?.[0]?.axisValue
                const bucketOf = (p) => {
                    const d = p.data?.rawMeasure?.date
                        ?? (typeof p.value?.[3] === 'object' ? p.value[3]?.date : undefined)
                        ?? p.value?.[0]
                    return typeof d === 'number' ? getDate(d, categoryFormat) : d
                }
                // Vraie valeur : rawMeasure pour séries classiques ; realValue post-MultiBand ; sinon value[1].
                const realValueOf = (p) => p.data?.rawMeasure?.value
                    ?? p.data?.realValue
                    ?? p.value?.[1]
                const isForecastParam = (p) => typeof p.value?.[3] === 'object' && p.value?.[3] !== null && typeof p.value?.[4] === 'string'
                const groupKeyOf = (p) => (isBarParams && isForecastParam(p))
                    ? `pred:${p.value[3].typeId}:${p.value[3].source}`
                    : `serie:${p.seriesIndex}`
                const grouped = groupBy(filtered, groupKeyOf)
                const collapsed = keys(grouped).map(key => {
                    const group = grouped[key]
                    if (isForecastParam(group[0]) && group.length > 1) {
                        const inBucket = isBarParams ? group.filter(p => bucketOf(p) === hoveredCategory) : group
                        const members = inBucket.length ? inBucket : group
                        const values = members.map(realValueOf).filter(v => v !== null && v !== undefined)
                        const first = members[0]
                        return {
                            seriesIndex: first.seriesIndex,
                            marker: first.marker,
                            seriesName: `${first.value[3]?.source ?? first.seriesName} (${i18n.average})`,
                            value: values.length ? mean(values) : undefined,
                            status: '',
                        }
                    }
                    const preferred = isBarParams ? (group.find(p => bucketOf(p) === hoveredCategory) ?? group[0]) : group[0]
                    return {
                        seriesIndex: preferred.seriesIndex,
                        marker: preferred.marker,
                        seriesName: preferred.seriesName,
                        value: realValueOf(preferred),
                        status: preferred.data?.rawMeasure ? ReactDOMServer.renderToString(statusIcon(preferred.data.rawMeasure, 20)) : '',
                    }
                })
                const lines = orderBy(collapsed, 'seriesIndex', 'asc')
                const body = lines
                    .filter(o => o.value !== null && o.value !== undefined)
                    .map(o => `<span style="display:flex;align-items:center;">${o.marker} ${o.seriesName} : ${round(o.value, 3)} ${unitLabel}<span style="padding-left:5px">${o.status}</span></span>`)
                    .join('')
                return `${dateStr} <br /> ${body} `
            },
        },
        grid: hasEvents
            ? [
                { top: 130, right: 20 + (associationsAxis.length * 60), bottom: 110, left: 100 },
                { top: 90, right: 20 + (associationsAxis.length * 60), height: 40, left: 100 },
            ]
            : [{ top: 65, right: 20 + (associationsAxis.length * 60), bottom: 110, left: associationsAxis.length > 0 ? 100 : 60 }],
        xAxis: [
            {
                type: 'category',
                position: 'bottom',
                data: categoryDates,
                gridIndex: 0,
                axisLabel: { show: true, rotate: 45 },
                axisLine: { show: true },
                axisTick: { show: true },
                splitLine: { show: chartOptions.displayXGrid !== false },
                ...(isBar ? {} : { axisPointer: { show: false } }),
            },
            {
                type: 'time',
                position: 'bottom',
                min: chartMinDate,
                max: chartMaxDate,
                gridIndex: 0,
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
            },
            ...(hasEvents ? [{
                type: 'time',
                position: 'bottom',
                min: chartMinDate,
                max: chartMaxDate,
                gridIndex: 1,
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
            }] : []),
        ],
        yAxis: [
            {
                type: 'value',
                name: `${primary.infoStat?.label} ${unitLabel}`,
                nameLocation: 'middle',
                nameGap: 40,
                gridIndex: 0,
                splitLine: { show: chartOptions.displayYGrid !== false },
                axisLabel: { formatter: nFormatter },
                ...getYScaleConfig(chartOptions),
            },
            ...(hasEvents ? [
                {
                    type: 'value',
                    minInterval: 1,
                    position: 'right',
                    axisLabel: { show: false },
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    gridIndex: 1,
                },
                {
                    type: 'category',
                    data: [i18n.events],
                    position: 'left',
                    gridIndex: 1,
                },
            ] : []),
            ...associationYAxes,
        ],
        axisPointer: { link: [{ xAxisIndex: 'all' }] },
        dataZoom: [
            {
                type: 'inside',
                xAxisIndex: [0, 1, ...(hasEvents ? [2] : [])],
                filterMode: 'none',
            },
            {
                type: 'slider',
                xAxisIndex: [0, 1, ...(hasEvents ? [2] : [])],
                bottom: 5,
                height: 18,
                filterMode: 'none',
            },
        ],
        legend: { bottom: 35, type: 'scroll', data: legendData },
        toolbox: {
            show: true,
            right: 65,
            feature: {
                saveAsImage: { title: i18n.export, icon: exportPictureIcon },
                myToolExport: {
                    show: true,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => exportFile({
                        data: getExportData(),
                        exportType: 'xlsx',
                        titleFile: i18n.overview,
                    }),
                },
                myToolLine: {
                    show: true,
                    title: i18n.curve,
                    icon: lineIcon,
                    onclick: () => setTypeLineValue(LINE),
                },
                myToolBar: {
                    show: true,
                    title: i18n.histogram,
                    icon: histogramIcon,
                    onclick: () => setTypeLineValue(BAR),
                },
            },
        },
        series,
    }

    return (
        <EChartV6Pluvio option={option} style={{ height: window.innerHeight - 140 }} />
    )
}

Graphic.propTypes = {
    pluviometer: PropTypes.instanceOf(PluviometerDto),
    primary: PropTypes.shape({
        measures: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluvioMeasure)),
        infoStat: PropTypes.instanceOf(DtoPluviometerStats),
    }),
    enveloppeMin: PropTypes.shape({
        measures: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluvioMeasure)),
    }),
    enveloppeMax: PropTypes.shape({
        measures: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluvioMeasure)),
    }),
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    cumulPluvio: PropTypes.string,
    showEnveloppe: PropTypes.bool,
    statsMeasures: PropTypes.shape({}),
    seriesYears: PropTypes.arrayOf(PropTypes.shape({})),
    thresholdLines: PropTypes.arrayOf(PropTypes.shape({})),
    modelData: PropTypes.shape({
        predMeasures: PropTypes.shape({}),
        selectedPred: PropTypes.shape({}),
        predStats: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    chartOptions: PropTypes.shape({
        displayXGrid: PropTypes.bool,
        displayYGrid: PropTypes.bool,
        yScaleMode: PropTypes.string,
        yMin: PropTypes.number,
        yMax: PropTypes.number,
        yInterval: PropTypes.number,
        modeDate: PropTypes.string,
    }),
    displayEvents: PropTypes.bool,
    stationEvents: PropTypes.arrayOf(PropTypes.shape({})),
    associationsSeries: PropTypes.arrayOf(PropTypes.shape({})),
    associationsAxis: PropTypes.arrayOf(PropTypes.shape({})),
    time: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

const GraphGenericPluvio = ({
    pluviometer,
    stat,
    showEnveloppe = false,
    pluviometerStatistics,
    paramsFrequencyStats,
    setParamsFrequencyStats,
    seriesYears = [],
    thresholdLines = [],
    envelopeGroup = 'auto',
    modelData = { predMeasures: {}, selectedPred: undefined, predStats: [] },
    chartOptions = {},
    displayEvents = true,
    associationsSeries = [],
    associationsAxis = [],
    onChangeDatesParent = () => {},
    onEnvelopeGroupChange = () => {},
    onCumulPluvioChange = () => {},
}) => {
    const stationEvents = useSelector(state => state.EventsReducer.stationEvents)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)
    const [displayModes, setDisplayModes] = useState(INITIAL_DISPLAY_MODES)
    const [minDate, setMinDate] = useState(moment().startOf('year').valueOf())
    const [maxDate, setMaxDate] = useState(moment().endOf('year').valueOf())
    const [time, setTime] = useState(CIVIL_YEAR)
    const [statsMeasures, setStatsMeasures] = useState({})
    const [progressFrequencyStats, setProgressFrequencyStats] = useState(100)

    const cumulPluvio = useMemo(() => {
        const group = keys(displayModes).find(mode => displayModes[mode])
        const dataType = stat || DATATYPE_RAIN
        if (dataType !== DATATYPE_RAIN) return ALL
        return group === AUTO ? getAutoCmumul(minDate - 1, maxDate) : getGroupFunc(group, displayModes)
    }, [displayModes, stat, minDate, maxDate])

    useEffect(() => {
        onCumulPluvioChange(cumulPluvio)
    }, [cumulPluvio])

    const dispatch = useDispatch()
    const pluvioMeasures = useSelector(state => state.PluviometryReducer.pluvioMeasures)

    const infoStat = pluviometerStatistics.find(s => s.typeId === stat)

    // useEffect (et non useUpdateEffect) : le fetch initial doit partir au montage. Le graphe
    // est monté une fois `stat` déjà défini (loader parent sur statsLoaded), donc un
    // useUpdateEffect sauterait le montage et ne déclencherait jamais le premier chargement.
    // Le garde `if (!stat) return` évite un fetch tant que le type de donnée n'est pas prêt.
    useEffect(() => {
        if (!stat) return
        setDataLoaded(false)
        const dataType = stat || DATATYPE_RAIN
        const group = keys(displayModes).find(mode => displayModes[mode])
        const baseInput = {
            stationId: pluviometer.id,
            dataType,
            chartMode: false,
            // On ne charge que la fenêtre sélectionnée par les onglets de temps.
            // minDate/maxDate null = onglet "tout l'historique" : pas de borne (fetch complet).
            ...(isNull(minDate) ? {} : { startDate: minDate }),
            ...(isNull(maxDate) ? {} : { endDate: maxDate }),
        }
        const rainGroupFunc = group === AUTO ? getAutoCmumul(minDate - 1, maxDate) : getGroupFunc(group, displayModes)
        const groupFunc = dataType === DATATYPE_RAIN ? rainGroupFunc : ALL
        const inputs = [{ ...baseInput, groupFunc }]
        if (showEnveloppe) {
            const autoEnveloppeFunc = dataType === DATATYPE_RAIN ? getGroupFuncFrequencyStat(group) : getGroupFunc(group, displayModes)
            const enveloppeGroupFunc = envelopeGroup && envelopeGroup !== 'auto' ? getGroupFunc(envelopeGroup, displayModes) : autoEnveloppeFunc
            inputs.push(
                { ...baseInput, groupFunc: enveloppeGroupFunc, enveloppe: ENVELOPPE_MIN },
                { ...baseInput, groupFunc: enveloppeGroupFunc, enveloppe: ENVELOPPE_MAX },
            )
        }
        dispatch(PluviometryAction.loadPluvioChartChronicMeasures(inputs, setProgress)).then(() => setDataLoaded(true))
    }, [maxDate, minDate, displayModes, pluviometer, stat, envelopeGroup, showEnveloppe, dispatch])

    useUpdateEffect(() => {
        if (!paramsFrequencyStats.showPreviousYears.length) {
            setStatsMeasures({})
            setProgressFrequencyStats(100)
            return
        }
        const lengthFrequencyStats = paramsFrequencyStats.showPreviousYears.length
        const group = keys(displayModes).find(mode => displayModes[mode])
        setProgressFrequencyStats(0)
        setStatsMeasures({})
        const dataType = stat || DATATYPE_RAIN
        paramsFrequencyStats.showPreviousYears.forEach(m => {
            dispatch(PluviometryAction.fetchPluvioChronicMeasures({
                stationId: pluviometer.id,
                dataType,
                groupFunc: dataType === DATATYPE_RAIN ? getGroupFuncFrequencyStat(group) : getGroupFunc(group, displayModes),
                enveloppe: m.toUpperCase().replace('FRE', 'PERIOD'),
                chartMode: true,
                startDate: minDate - 1,
                endDate: maxDate,
            })).then(result => {
                setStatsMeasures(prev => ({ ...prev, [m]: result }))
                setProgressFrequencyStats(prev => prev + (100 / lengthFrequencyStats))
            })
        })
    }, [paramsFrequencyStats, displayModes, stat, minDate, maxDate, pluviometer])

    const measures = useMemo(() => pluvioMeasures[0]?.measures, [pluvioMeasures])

    // histoYears est dérivé de l'amplitude de l'historique disponible pour le type
    // de données (infoStat.startDate/endDate), et non plus des mesures affichées :
    // celles-ci sont désormais bornées à la fenêtre des onglets de temps. On garde
    // la même forme de calcul (différence en années - 1). `paramsFrequencyStats.measures`
    // n'est consommé nulle part (seul histoYears l'est), on ne le met donc plus à jour.
    useEffect(() => {
        if (!infoStat?.startDate || !infoStat?.endDate) return
        const histoYears = moment.duration(moment(infoStat.endDate).diff(moment(infoStat.startDate))).years() - 1
        if (histoYears !== paramsFrequencyStats.histoYears) {
            setParamsFrequencyStats(prev => ({ ...prev, histoYears }))
        }
    }, [infoStat])

    useEffect(() => {
        const dataType = stat || DATATYPE_RAIN
        const group = keys(displayModes).find(mode => displayModes[mode])
        const autoEnveloppeFunc = dataType === DATATYPE_RAIN
            ? getGroupFuncFrequencyStat(group)
            : getGroupFunc(group, displayModes)
        const effectiveFunc = envelopeGroup && envelopeGroup !== 'auto'
            ? getGroupFunc(envelopeGroup, displayModes)
            : autoEnveloppeFunc
        onEnvelopeGroupChange(groupFuncToLabel(effectiveFunc))
    }, [stat, displayModes, envelopeGroup])

    // Pas d'early return sur `!pluvioMeasures.length` : au rechargement complet le store
    // est vide, et court-circuiter ici afficherait "aucune donnée" au lieu de la carte de
    // chargement. Le rendu principal ci-dessous gère déjà les 3 états (chargement / vide /
    // graphe) et laisse les onglets visibles pendant le fetch.
    const isLoading = !dataLoaded || round(progressFrequencyStats) !== 100
    const currentProgress = !dataLoaded ? progress : progressFrequencyStats

    const handleChangeTime = ({ minDate: newMin, maxDate: newMax, time: newTime }) => {
        setTime(newTime)
        setMinDate(newMin ?? null)
        setMaxDate(newMax ?? moment().valueOf())
        onChangeDatesParent({ minDate: newMin, maxDate: newMax, tab: newTime })
    }

    return (
        <>
            <Grid container alignItems='center' sx={{ paddingLeft: '10px', paddingTop: '5px' }} spacing={1}>
                <Grid>
                    <MUIChartTabs
                        time={time}
                        onChangeTime={handleChangeTime}
                        stationType={STATION_TYPE_NAME.pluviometry}
                        statistics={pluviometerStatistics}
                        withSuiviTabs
                    />
                </Grid>
                <Grid>
                    <SimpleChartTabsPluvio
                        displayModes={displayModes}
                        displayModesUniqChoise
                        changeParent={setDisplayModes}
                    />
                </Grid>
            </Grid>
            {isLoading && (
                <div style={{ paddingTop: 60 }}>
                    <ProgressCard progress={currentProgress} />
                </div>
            )}
            {!isLoading && !measures?.length && (
                <div style={{ paddingTop: 45 }}>
                    <MessageCard>{i18n.noDataToDisplay}</MessageCard>
                </div>
            )}
            {!isLoading && !!measures?.length && (
                <Graphic
                    pluviometer={pluviometer}
                    primary={{ measures, infoStat }}
                    enveloppeMin={{ measures: pluvioMeasures[1]?.measures ?? [] }}
                    enveloppeMax={{ measures: pluvioMeasures[2]?.measures ?? [] }}
                    showEnveloppe={showEnveloppe}
                    minDate={minDate}
                    maxDate={maxDate}
                    cumulPluvio={cumulPluvio}
                    statsMeasures={statsMeasures}
                    seriesYears={seriesYears}
                    thresholdLines={thresholdLines}
                    modelData={modelData}
                    chartOptions={chartOptions}
                    displayEvents={displayEvents}
                    stationEvents={stationEvents}
                    associationsSeries={associationsSeries}
                    associationsAxis={associationsAxis}
                    time={time}
                />
            )}
        </>
    )
}

GraphGenericPluvio.propTypes = {
    pluviometer: PropTypes.instanceOf(PluviometerDto),
    stat: PropTypes.instanceOf(DtoPluviometerStats),
    pluviometerStatistics: PropTypes.arrayOf(DtoPluviometerStats),
    showEnveloppe: PropTypes.bool,
    paramsFrequencyStats: PropTypes.shape({
        measures: PropTypes.instanceOf(DtoPluvioMeasure),
        showPreviousYears: PropTypes.arrayOf(PropTypes.string),
        histoYears: PropTypes.number,
    }),
    setParamsFrequencyStats: PropTypes.func,
    onChangeDatesParent: PropTypes.func,
    onEnvelopeGroupChange: PropTypes.func,
    onCumulPluvioChange: PropTypes.func,
    seriesYears: PropTypes.arrayOf(PropTypes.shape({})),
    thresholdLines: PropTypes.arrayOf(PropTypes.shape({})),
    envelopeGroup: PropTypes.string,
    modelData: PropTypes.shape({
        predMeasures: PropTypes.shape({}),
        selectedPred: PropTypes.shape({}),
        predStats: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    chartOptions: PropTypes.shape({
        displayXGrid: PropTypes.bool,
        displayYGrid: PropTypes.bool,
        yScaleMode: PropTypes.string,
        yMin: PropTypes.number,
        yMax: PropTypes.number,
        yInterval: PropTypes.number,
        modeDate: PropTypes.string,
    }),
    displayEvents: PropTypes.bool,
    associationsSeries: PropTypes.arrayOf(PropTypes.shape({})),
    associationsAxis: PropTypes.arrayOf(PropTypes.shape({})),
}

export default GraphGenericPluvio
